import * as React from "react";

import {
    ApolloGqlClientImpl,
    ApolloGqlError,
    fromApolloNetworkStatus,
    type ApolloQueryOptions,
    type ApolloResult,
    type AsyncApolloQueryResult,
} from "@khan/data-gql-apollo";
import {documentNode, type GqlOperation} from "@khan/data-gql-core";

import type {FetchMoreOptions} from "./apollo-types.ts";
import ApolloQueryCore, {
    type QueryResult,
} from "./internal/apollo-query-core.tsx";
import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "./with-apollo.tsx";

type OwnProps<
    TData,
    TVariables extends object,
> = ApolloQueryOptions<TVariables> & {
    query: GqlOperation<TData, TVariables>;
    children: (
        result: AsyncApolloQueryResult<TData, TVariables>,
    ) => React.ReactNode;
    onCompleted?: (data?: TData) => void | null | undefined;
    onError?: (error: ApolloGqlError) => void | null | undefined;
    pollInterval?: number;
    notifyOnNetworkStatusChange?: boolean;
};

type Props<TData, TVariables extends object> = OwnProps<TData, TVariables> &
    WithApolloProps;

class ApolloQueryImpl<
    TData extends Record<any, any>,
    TVariables extends Record<any, any>,
> extends React.Component<Props<TData, TVariables>> {
    _fromApolloResultToApolloResult(
        result: QueryResult<TData, TVariables>,
    ): AsyncApolloQueryResult<TData, TVariables> {
        const {error, data, loading, networkStatus, fetchMore, refetch} =
            result;

        const gqlRefetch = async (
            variables?: TVariables,
        ): Promise<ApolloResult<TData>> => {
            const {data} = await refetch(variables);
            return {
                data,
            };
        };

        const gqlFetchMore = async ({
            updateQuery,
            variables,
        }: FetchMoreOptions<TData, TVariables>): Promise<
            ApolloResult<TData>
        > => {
            const {data} = await fetchMore({
                // @ts-expect-error - TS2322 - Type '(previousQueryResult: TData, options: FetchMoreUpdateQueryOptions<TData, TVariables>) => TData' is not assignable to type '(previousQueryResult: TData, options: { fetchMoreResult?: TData | undefined; variables?: TVariables | undefined; }) => TData'.
                updateQuery,
                variables: {
                    ...variables,
                },
            });
            return {
                data,
            };
        };

        const gqlNetworkStatus =
            networkStatus == null
                ? undefined
                : fromApolloNetworkStatus(networkStatus);

        return {
            error: error == null ? undefined : new ApolloGqlError(error),
            data,
            loading,
            networkStatus: gqlNetworkStatus,
            refetch: gqlRefetch,
            fetchMore: gqlFetchMore,
        };
    }

    _renderQuery(): React.ReactNode {
        const {
            gqlClient,
            children,
            query,
            targetLocale,
            curriculum,
            onCompleted,
            onError: maybeOnErrorFromProps,
            ...rest
        } = this.props;

        // We need to map the ApolloError to a ApolloGqlError, but we only want to
        // add a handler if we were given one.
        const onError =
            maybeOnErrorFromProps == null
                ? undefined
                : (apolloError: Error) =>
                      maybeOnErrorFromProps(new ApolloGqlError(apolloError));

        const coreClient = ApolloGqlClientImpl.asApolloGqlClient(gqlClient);
        const context = ApolloGqlClientImpl.getContext({
            targetLocale,
            curriculum,
        });

        return (
            <ApolloQueryCore
                {...rest}
                client={coreClient._getClient(context)}
                query={documentNode(query)}
                context={context}
                onError={onError}
                onCompleted={onCompleted}
            >
                {(result) =>
                    children(this._fromApolloResultToApolloResult(result))
                }
            </ApolloQueryCore>
        );
    }

    render(): React.ReactNode {
        /**
         * TODO(somewhatabstract, FEI-3709): The React Apollo Query renders the
         * children with an empty result when this.props.skip is true. We are
         * retaining that behavior for now to ensure we don't introduce a new
         * bug while we transition off React Apollo, but we should consider
         * removing it and adding the following here instead.
         *
         * ```js
         * if (this.props.skip) {
         *     return null;
         * }
         * ```
         */
        return this._renderQuery();
    }
}

const ApolloQueryWithContext = withApollo(ApolloQueryImpl);
export default function <
    TData extends Record<any, any>,
    TVariables extends Record<any, any>,
>(props: WithoutApollo<Props<TData, TVariables>>): React.ReactElement {
    // We know that the generic escapes here, but I can't work out a way to
    // avoid that when using an HOC around a genericly parameterized component.
    // We could likely define HOCs to accommodate this but then we'd lose the
    // handling of default props from React.ElementConfig. I'm not sure there's
    // a way to define a new `React.ElementConfig` that would work, so for now
    // we suppress the error :(
    return <ApolloQueryWithContext {...props} />;
}
